<template>
  <div class="fixed inset-0 -z-10">
    <div
      ref="mapContainer"
      :data-journey="activeJourney ?? undefined"
      class="h-full w-full"
      @contextmenu.prevent
    />
  </div>

  <div aria-hidden="true">
    <Teleport v-for="m in allIconMounts" :key="m.key" :to="m.el">
      <RevIllustration
        :alt="m.alt"
        :height="m.height"
        image-class-names="block"
        loading="eager"
        :src="m.src"
        :width="m.width"
      />
    </Teleport>
  </div>

  <!-- Gone where the phone's card docks, in CSS: `isPhone` is false through SSR, so a
         `v-if` here mismatched on hydration and flickered on a deep link. -->
  <MapZoomControl
    :class="activeJourney && 'max-md:hidden'"
    @zoom-in="zoomIn"
    @zoom-out="zoomOut"
  />
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevIllustration } from '@ds/components/Illustration'
import type { Map as MapboxMap, Marker as MapboxMarker } from 'mapbox-gl'

import translations from '~/scopes/campaign/components/BGMap.translations'
import MapZoomControl from '~/scopes/campaign/components/MapZoomControl.vue'
import { useIntroState } from '~/scopes/campaign/composables/useIntroState'
import { useIsDesktop } from '~/scopes/campaign/composables/useIsDesktop'
import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { useJourneyCamera } from '~/scopes/campaign/composables/useJourneyCamera'
import { useMapMarkerOrigin } from '~/scopes/campaign/composables/useMapMarkerOrigin'
import { useMapReady } from '~/scopes/campaign/composables/useMapReady'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import {
  MODAL_ROUTES,
  type ModalRoute,
  isImpactRoute,
} from '~/scopes/campaign/const/config.const'
import { ARTICLES, type ImpactType } from '~/scopes/campaign/data/articles/data'
import { journeyFor, stopAt } from '~/scopes/campaign/data/journeys/data'
import type { JourneyStopLocation } from '~/scopes/campaign/data/journeys/types'
import { METALS } from '~/scopes/campaign/data/metals/data'
import {
  MARKER_BUBBLE_SIZE,
  type MarkerIconMount,
  createMapMarker,
} from '~/scopes/campaign/utils/createMapMarker'
import { prefersReducedMotion } from '~/scopes/campaign/utils/prefersReducedMotion'

const {
  public: { MAPBOX_API_KEY: MAPBOX_TOKEN },
} = useRuntimeConfig()

const IMPACT_META: Record<ImpactType, { icon: string; route: ModalRoute }> = {
  environmental: {
    icon: '/mine-my-phone/icons/plant--emoji.svg',
    route: MODAL_ROUTES.ENVIRONMENTAL_IMPACT,
  },
  human: {
    icon: '/mine-my-phone/icons/group--emoji.svg',
    route: MODAL_ROUTES.HUMAN_IMPACT,
  },
  geopolitical: {
    icon: '/mine-my-phone/icons/world--emoji.svg',
    route: MODAL_ROUTES.GEOPOLITICAL_IMPACT,
  },
}

const i18n = useI18n()
const locale = useI18nLocale()
const {
  activeModal,
  activeDetail,
  quickMetal,
  quickMine,
  activeJourney,
  journeyStep,
  openMetalQuickView,
  navigateToArticle,
  navigateToHome,
} = useModalState()
const { landedStop } = useJourneyCamera()
const { showIntro } = useIntroState()
const { markMapReady } = useMapReady()
const route = useRoute()

// Bare home = no panel and no quick peek open — the only state we auto-frame the world for (on map load and on intro dismiss).
const isBareHome = () => activeModal.value === null && quickMetal.value === null

// Responsive tiers (see config.const): phone <768, tablet 768–1199, desktop ≥1200.
// `isDesktop` gates the dual-rail camera padding; `isPhone` gates the mobile camera
// branch and the gesture-to-dismiss. Tablet (neither) frames like desktop but with
// a single column, whichever side the one panel on screen is on. The map is client-only, so reading these imperatively
// (never in the template) raises no hydration concern.
const isDesktop = useIsDesktop()
const isPhone = useIsPhone()
// A marker tap leaves the sheet at its resting level, so the map stays visible.
const { openedFromMarker, markMarkerOrigin } = useMapMarkerOrigin()
const { trackClick } = useTracking()

// The mapbox-gl namespace. An import type because the library touches `window` at
// load, so the module itself is only pulled in on mount — never statically.
type MapboxGL = typeof import('mapbox-gl').default

type MarkerEntry = { el: HTMLElement; marker: MapboxMarker }
type IconTeleportEntry = { key: string } & MarkerIconMount

type MetalMarkerEntry = {
  el: HTMLElement
  slug: string
  mine: string
  marker: MapboxMarker
  icons: MarkerIconMount[]
}

type ArticleMarkerEntry = {
  el: HTMLElement
  /** The article's slug, i.e. what `activeDetail` compares against. */
  articleSlug: string
  impact: ImpactType
  marker: MapboxMarker
  icons: MarkerIconMount[]
}

// One per *located* stop, built when a journey starts and torn down when it ends.
type JourneyMarkerEntry = {
  el: HTMLElement
  step: number
  marker: MapboxMarker
  icons: MarkerIconMount[]
}

const mapContainer = ref<HTMLElement | null>(null)
let map: MapboxMap | null = null
let mapboxgl: MapboxGL | null = null
const metalMarkers = shallowRef<MetalMarkerEntry[]>([])
const articleMarkers = shallowRef<ArticleMarkerEntry[]>([])
const journeyMarkers = shallowRef<JourneyMarkerEntry[]>([])

const allIconMounts = computed<IconTeleportEntry[]>(() => {
  const out: IconTeleportEntry[] = []
  metalMarkers.value.forEach((m, i) =>
    m.icons.forEach((ic, j) => out.push({ key: `metal-${i}-${j}`, ...ic })),
  )

  articleMarkers.value.forEach((m, i) =>
    m.icons.forEach((ic, j) => out.push({ key: `article-${i}-${j}`, ...ic })),
  )

  journeyMarkers.value.forEach((m, i) =>
    m.icons.forEach((ic, j) => out.push({ key: `journey-${i}-${j}`, ...ic })),
  )

  return out
})

// Marker-group selectors the sync logic frames / highlights.
const metalEntries = (slug: string | null) =>
  metalMarkers.value.filter((m) => m.slug === slug)
// The one mine within a metal (by its derived slug), as a 0-or-1 array to match the
// selectors above — empty when no `?mine=` is set or it doesn't resolve, so callers
// fall back to framing all of the metal's mines.
const metalMineEntries = (slug: string | null, mine: string | null) =>
  mine === null
    ? []
    : metalMarkers.value.filter((m) => m.slug === slug && m.mine === mine)
const articleEntries = (id: string | null) =>
  articleMarkers.value.filter((m) => m.articleSlug === id)
const impactEntries = (modal: ModalRoute) =>
  articleMarkers.value.filter((m) => IMPACT_META[m.impact].route === modal)

// eslint-disable-next-line @typescript-eslint/no-shadow
function createMetalMarkers(mapboxgl: MapboxGL, map: MapboxMap) {
  const list: MetalMarkerEntry[] = []

  for (const metal of METALS) {
    for (const mine of metal.mines) {
      const mineKey = mine.slug
      const { el, icons } = createMapMarker({
        icon: '/mine-my-phone/icons/pickaxe--emoji.svg',
        label: i18n(translations.mineLabel, {
          metal: i18n(metal.name),
        }),
        sublabel: i18n(mine.region),
        important: metal.important,
        onClick: () => openMetalQuickView(metal.slug, mineKey),
      })

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat(mine.coordinates)
        .addTo(map)

      list.push({
        el,
        slug: metal.slug,
        mine: mineKey,
        marker,
        icons,
      })
    }
  }

  metalMarkers.value = list
}

// Which impact's markers are built, so a re-sync within one journey doesn't rebuild them.
let journeyMarkersFor: string | null = null

function clearJourneyMarkers() {
  const snapshot = [...journeyMarkers.value]
  journeyMarkers.value = []
  for (const { marker } of snapshot) marker.remove()
  journeyMarkersFor = null
}

// Called from the sync itself, so "the markers exist" can't race "the camera wants one".
// eslint-disable-next-line @typescript-eslint/no-shadow
function refreshJourneyMarkers(mapboxgl: MapboxGL, map: MapboxMap) {
  const impact = activeJourney.value
  if (journeyMarkersFor === impact) return
  clearJourneyMarkers()
  const journey = journeyFor(impact)
  if (!journey || !impact) return

  const list: JourneyMarkerEntry[] = []
  journey.stops.forEach((stop, index) => {
    const { location } = stop
    if (!location?.pin) return
    // One icon for the whole run, the impact's own; no label, no `onClick`.
    const { el, icons } = createMapMarker({
      icon: IMPACT_META[impact].icon,
    })
    el.dataset.journeyMarker = ''

    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(location.coordinates)
      .addTo(map)

    list.push({ el, step: index + 1, marker, icons })
  })

  journeyMarkers.value = list
  journeyMarkersFor = impact
}

const WORLD_VIEW = { center: [0, 0] as [number, number], zoom: 2.5 }

// The map's zoom floor, per tier. A phone's narrow canvas shows far less of the
// world per zoom step, so it gets one extra step out (1) to keep the whole globe
// reachable; larger screens hold at 2.
const MIN_ZOOM_PHONE = 1
const MIN_ZOOM_DEFAULT = 2
const minZoomFor = (phone: boolean) =>
  phone ? MIN_ZOOM_PHONE : MIN_ZOOM_DEFAULT

// Target zoom when framing a single mine (a tapped marker). fitBounds on one point
// is a degenerate box, so it just centers the mine and zooms to this maxZoom —
// close enough to read the region without diving to street level.
const MINE_ZOOM = 5.5

// How far an active marker's bubble rises above its anchor (the mini dot), set by
// the `translateY(-85%)` on `.mine-marker.active .marker-lift` below. The anchor
// sits at the marker's foot while its visual mass floats this far above it, so
// tight framing centers the bubble rather than the anchor (see the mobile branch).
const MARKER_BUBBLE_RISE = MARKER_BUBBLE_SIZE * 0.85

// A px-valued CSS custom property off :root, with a fallback.
function cssPx(name: string, fallback: number) {
  const v = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(name),
  )

  return Number.isFinite(v) ? v : fallback
}

function rectOf(selector: string) {
  return document.querySelector(selector)?.getBoundingClientRect() ?? null
}

// Fraction of an axis that padding may consume — the rest is left for content so
// Mapbox doesn't reject a frame with no room.
const PAD_BUDGET = 0.85

// Mapbox rejects padding that leaves no room; scale each axis to fit the canvas
// while preserving its ratio.
function clampPadding(
  pad: { top: number; bottom: number; left: number; right: number },
  w: number,
  h: number,
) {
  const fit = (a: number, b: number, total: number) => {
    const limit = total * PAD_BUDGET
    const sum = a + b

    return sum <= limit ? [a, b] : [(a * limit) / sum, (b * limit) / sum]
  }
  const [left, right] = fit(pad.left, pad.right, w)
  const [top, bottom] = fit(pad.top, pad.bottom, h)

  return { top, bottom, left, right }
}

// Padding that keeps framed content — and the markers' own footprint — inside the
// viewport the open panels leave free. Panel sizes are *measured* from the DOM,
// never hardcoded, so a design tweak (panel width, header height) can't drift it
// out of sync. Breathing room is derived from the marker geometry: an active
// marker grows and translates up, reaching ~half a bubble to each side and ~its
// full height above its anchor, so we keep that clear of the panel/header edges.
// eslint-disable-next-line @typescript-eslint/no-shadow
function cameraPadding(map: MapboxMap) {
  const gap = cssPx('--raw-gappers', 16)
  const headerBottom = cssPx('--raw-header-height', 54)
  const half = MARKER_BUBBLE_SIZE / 2
  const side = gap + half // markers sit a gutter + half-bubble off any edge
  const above = gap + MARKER_BUBBLE_SIZE + half // taller: markers reach upward

  const c = map.getContainer()
  const w = c.clientWidth
  const h = c.clientHeight

  let top = headerBottom + above
  let bottom = side
  let left = side
  let right = side

  // No rails during a journey. Two aims, because the card is placed two ways.
  if (activeJourney.value) {
    // The phone's card docks and grows upward, so the marker's room is measured
    // off a twin of the *incoming* card — keyed by step, so a stale one falls
    // through to the fraction below.
    const dock = rectOf(`[data-journey-dock="${journeyStep.value}"]`)
    if (dock) {
      const bandTop = headerBottom + gap + half
      const bandBottom = dock.top - gap - MARKER_BUBBLE_RISE
      // Too short to centre in: clear the card first, since the glassless
      // header still reads through.
      const bubbleY = Math.min((bandTop + bandBottom) / 2, bandBottom)
      const targetY = bubbleY + MARKER_BUBBLE_RISE
      // The marker footprint is already inside `bandTop`, so the padding's only
      // job here is to offset the centre.
      let padTop = headerBottom
      let padBottom = Math.max(side, padTop + h - 2 * targetY)
      // Trim from both ends, never scale: the *difference* is what carries the
      // aim, and `clampPadding` preserves the ratio, which moves the centre.
      const excess = padTop + padBottom - h * PAD_BUDGET
      if (excess > 0) {
        const trim = Math.min(padTop, excess / 2)
        padTop -= trim
        padBottom -= trim
      }

      return { top: padTop, bottom: padBottom, left, right }
    }

    // No dock: there the card hangs *from* the marker, so the shared fraction is
    // the whole answer and measuring would be circular.
    const targetY =
      h * cssPx('--raw-journey-marker-y', 0.5) + MARKER_BUBBLE_RISE

    return clampPadding(
      { top, bottom: Math.max(side, top + h - 2 * targetY), left, right },
      w,
      h,
    )
  }

  if (!isPhone.value) {
    // Rail tiers. Inset from each rail's *width* + the gapper, never its live
    // left/right: they animate in on translateX and would misframe mid-slide.
    const impacts = rectOf('[data-map-inset="impacts"]')
    // First match is enough — library + `?metal=` share a side and a width.
    const highlights = rectOf('[data-map-inset="highlights"]')
    if (highlights) right = gap + highlights.width + side
    // Below lg the modal replaces the rail instead of coexisting with it.
    if (impacts && (isDesktop.value || !highlights))
      left = gap + impacts.width + side
  } else if (
    quickMetal.value !== null ||
    (activeModal.value === MODAL_ROUTES.METAL_LIBRARY &&
      activeDetail.value !== null)
  ) {
    // Frame the mine in the strip the sheet leaves above it — only a marker tap
    // gets here, so the sheet is at its resting level (see syncMobile).
    const sheet = rectOf('[data-map-inset="highlights"]')
    if (sheet) {
      top = headerBottom
      bottom = h - sheet.top - 1.5 * MARKER_BUBBLE_RISE
    }
  } else {
    // Mobile frames an article above the impacts sheet; the other moving case
    // (home) has no sheet in the way.
    const impactDetail =
      isImpactRoute(activeModal.value) && activeDetail.value !== null
    if (impactDetail) {
      const sheet = rectOf('[data-map-inset="impacts"]')
      if (sheet) {
        // The 1.5 centers the marker's *bubble*, which floats
        // MARKER_BUBBLE_RISE above its anchor, rather than the anchor.
        top = headerBottom
        bottom = h - sheet.top - 1.5 * MARKER_BUBBLE_RISE
      }
    }
  }

  return clampPadding({ top, bottom, left, right }, w, h)
}

// Panels animate in (slide / expand), so measuring the instant a route changes
// frames content under a panel that hasn't landed yet. Wait until the padding
// stops changing for a few frames, then move once with the settled geometry —
// rAF-based, so it couples to no animation duration and is a no-op (~50ms) when
// nothing is animating. `move` is handed the map it settled on — narrowing can't
// reach into a callback.
// Two fast state changes used to queue two rAF loops that both moved the camera.
let settleId = 0

function moveCameraWhenStable(
  move: (map: MapboxMap, pad: ReturnType<typeof cameraPadding>) => void,
) {
  if (!map) return
  // eslint-disable-next-line no-plusplus
  const id = ++settleId
  let prev = ''
  let stable = 0
  let frames = 0
  const tick = () => {
    // Re-checked each frame: unmount, or a newer request, cancels this one.
    if (!map || id !== settleId) return
    const pad = cameraPadding(map)
    const key = `${Math.round(pad.top)},${Math.round(pad.bottom)},${Math.round(pad.left)},${Math.round(pad.right)}`
    stable = key === prev ? stable + 1 : 0
    prev = key
    if (stable >= 3 || frames >= 60) {
      move(map, pad)

      return
    }
    frames += 1
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

function flyToWorld() {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  moveCameraWhenStable((map, padding) =>
    map.flyTo({ ...WORLD_VIEW, padding, duration: 1800, essential: true }),
  )
}

// Frame a marker group within the available viewport.
function fitMarkers(entries: MarkerEntry[], maxZoom: number) {
  if (!map || !mapboxgl || entries.length === 0) return
  const bounds = new mapboxgl.LngLatBounds()
  for (const { marker } of entries) bounds.extend(marker.getLngLat())
  // eslint-disable-next-line @typescript-eslint/no-shadow
  moveCameraWhenStable((map, padding) =>
    map.fitBounds(bounds, {
      padding,
      maxZoom,
      duration: 1500,
      essential: true,
    }),
  )
}

// Highlight a marker group and frame it.
function focusEntries(entries: MarkerEntry[], maxZoom: number) {
  setActiveMarkers(entries)
  fitMarkers(entries, maxZoom)
}

// Frame a metal detail (quick view or library detail): fly to the single tapped mine
// when `?mine=` resolves, else fit all of the metal's mines.
function focusMetalDetail(slug: string, mine: string | null) {
  const mineEntries = metalMineEntries(slug, mine)
  if (mineEntries.length) focusEntries(mineEntries, MINE_ZOOM)
  else focusEntries(metalEntries(slug), 6)
}

// Screenfuls per second (1.2 is Mapbox's default), so each flight is sized by its own
// distance; the curve is the arc's peak altitude.
const JOURNEY_FLY_SPEED = 2.4
const JOURNEY_FLY_CURVE = 1.42

type JourneyFlight = { duration?: number; speed?: number; curve?: number }

function moveJourneyCamera(
  move: (
    map: MapboxMap,
    pad: ReturnType<typeof cameraPadding>,
    flight: JourneyFlight,
  ) => void,
) {
  // `duration` is the documented override for `speed` — never `maxDuration`, which
  // past its limit sets the duration to 0 and teleports.
  const flight: JourneyFlight = prefersReducedMotion()
    ? { duration: 0 }
    : { speed: JOURNEY_FLY_SPEED, curve: JOURNEY_FLY_CURVE }
  // eslint-disable-next-line @typescript-eslint/no-shadow
  moveCameraWhenStable((map, padding) => move(map, padding, flight))
}

// Same framing on every tier — there are no panels, only the padding differs.
function syncJourney(step: number) {
  if (!map || !mapboxgl) return
  refreshJourneyMarkers(mapboxgl, map)
  const journey = journeyFor(activeJourney.value)
  if (!journey) return
  const stop = stopAt(journey, step)
  if (!stop) return

  // Authored framing wins, pin or no pin.
  if (stop.location) {
    flyToStop(stop.location, step)

    return
  }

  // Unauthored last stop: hold, and leave `landedStop` where the camera really is.
  if (step === journey.stops.length) return

  // Unauthored opening beat: frame the whole route, and don't make the card wait.
  landedStop.value = step
  setActiveMarkers([])
  fitJourney()
}

function flyToStop(location: JourneyStopLocation, step: number) {
  const entry = journeyMarkers.value.find((m) => m.step === step) ?? null
  // eslint-disable-next-line @typescript-eslint/no-shadow
  moveJourneyCamera((map, padding, flight) => {
    map.flyTo({
      center: location.coordinates,
      zoom: location.zoom,
      padding,
      essential: true,
      ...flight,
    })
    // The marker changes hands on arrival, so the stop you left stays lit through
    // the flight and the new one plays its hover treatment as the card enters.
    const land = () => {
      if (journeyStep.value !== step) return
      setActiveMarkers(entry ? [entry] : [])
      landedStop.value = step
    }
    // Bound after the call, which fires its own `moveend` when it interrupts a
    // flight and lands synchronously at duration 0.
    if (map.isMoving()) map.once('moveend', land)
    else land()
  })
}

function fitJourney() {
  if (!mapboxgl || journeyMarkers.value.length === 0) return
  const bounds = new mapboxgl.LngLatBounds()
  for (const { marker } of journeyMarkers.value)
    bounds.extend(marker.getLngLat())
  // eslint-disable-next-line @typescript-eslint/no-shadow
  moveJourneyCamera((map, padding, flight) =>
    map.fitBounds(bounds, {
      padding,
      maxZoom: 5,
      essential: true,
      ...flight,
    }),
  )
}

// Mark exactly these entries active; the CSS mutes every other marker.
function setActiveMarkers(entries: MarkerEntry[]) {
  const active = new Set(entries.map((e) => e.el))
  for (const { el } of [
    ...metalMarkers.value,
    ...articleMarkers.value,
    ...journeyMarkers.value,
  ]) {
    el.classList.toggle('active', active.has(el))
  }
}

// eslint-disable-next-line @typescript-eslint/no-shadow
function createArticleMarkers(mapboxgl: MapboxGL, map: MapboxMap) {
  const list: ArticleMarkerEntry[] = []

  for (const article of ARTICLES) {
    for (const region of article.region) {
      const { el, icons } = createMapMarker({
        icon: IMPACT_META[region.impact].icon,
        label: i18n(translations.storyLabel, {
          metal: i18n(article.metal.name),
        }),
        // The region's site name, or its country when no site is named.
        sublabel: i18n(region.name ?? region.country),
        onClick: () => {
          markMarkerOrigin(article.slug)
          navigateToArticle(article)

          trackClick({
            routeName: route.name,
            zone: 'open-article-quick-view',
            article: article.slug,
            campaign: 'raw-materials',
          })
        },
      })

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat(region.coordinates)
        .addTo(map)

      list.push({
        el,
        articleSlug: article.slug,
        impact: region.impact,
        marker,
        icons,
      })
    }
  }

  articleMarkers.value = list
}

// Basemap label categories, toggled per-category. This style imports Mapbox Standard
// as the `basemap` fragment, whose labels aren't removable layers — they're set through
// the config API (applied on load). Flip a value to show/hide a category.
// Note: Standard groups countries + states + cities under the single `showPlaceLabels`
// switch, so there's no native countries-only split (that needs a custom label layer).
const BASEMAP_LABELS: Record<string, boolean> = {
  showPlaceLabels: true,
  showRoadLabels: false,
  showPointOfInterestLabels: false,
  showTransitLabels: false,
}

onMounted(async () => {
  mapboxgl = (await import('mapbox-gl')).default

  mapboxgl.accessToken = MAPBOX_TOKEN
  // mapboxgl.config.EVENTS_URL = ''

  // Start at the floor: a start zoom above minZoom snaps down on the first paint
  // (a visible flick), so keep the two equal.
  const minZoom = minZoomFor(isPhone.value)

  map = new mapboxgl.Map({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container: mapContainer.value!,
    style: 'mapbox://styles/backmarket/cmu5ecjkj003o01s7e8x1g4dd',
    center: [10, 30],
    // keep map in English for de-de
    language: locale === 'de-de' ? 'en' : toBcp47Locale(locale),
    zoom: minZoom,
    minZoom,
    maxZoom: 18,
    projection: 'mercator',
    attributionControl: false,
    logoPosition: 'bottom-right',
    renderWorldCopies: true,

    dragRotate: false, // Disable map mouse tilt and rotation
    touchPitch: false, // Disable map touch tilt
  })

  // Disable map touch rotation
  map.touchZoomRotate.disableRotation()
  // Disable one finger drag zoom
  map.touchZoomRotate.disableTapDragZoom()

  // Phone only: any gesture on the map (tap, pan, pinch) means "let me see the
  // map" — dismiss whatever is open by navigating home (panels are URL-driven).
  // Route change only: the camera stays wherever the gesture puts it. Marker taps
  // never land here (they stop propagation in createMapMarker). Tablet/desktop
  // keep the persistent left rail, so a map gesture must not dismiss it.
  const closePanelsForGesture = () => {
    // A journey is not a panel; a stray tap must not end the run.
    if (activeJourney.value) return
    if (!isPhone.value || showIntro.value || isBareHome()) return
    navigateToHome()
  }
  map.on('click', closePanelsForGesture)
  map.on('dragstart', closePanelsForGesture)

  map.addControl(
    new mapboxgl.AttributionControl({ compact: true }),
    'bottom-right',
  )

  map.on('load', () => {
    // Both are set by the time load fires; the check is for the type-checker.
    if (!map || !mapboxgl) return

    // Apply the basemap label choices (see BASEMAP_LABELS).
    for (const [prop, visible] of Object.entries(BASEMAP_LABELS)) {
      map.setConfigProperty('basemap', prop, visible)
    }

    createMetalMarkers(mapboxgl, map)
    createArticleMarkers(mapboxgl, map)
    // Defer camera sync while intro is showing so movements don't happen behind the modal blur.
    if (!showIntro.value) {
      syncMap(
        activeModal.value,
        activeDetail.value,
        quickMetal.value,
        quickMine.value,
      )
      if (isBareHome()) flyToWorld()
    }

    // Real map load (style + first tiles painted) — lift the loader curtain.
    markMapReady()
  })
})

function syncMap(
  modal: ModalRoute | null,
  detail: string | null,
  quick: string | null,
  mine: string | null,
) {
  if (!map) return
  // The journey replaces the panels, so nothing below it can be on screen to frame.
  if (activeJourney.value && journeyStep.value) {
    syncJourney(journeyStep.value)

    return
  }
  if (journeyMarkersFor !== null) clearJourneyMarkers()
  // Tablet frames like desktop (fit markers — there's map to the right of the
  // left column); only the phone tier uses the strip-above-the-sheet logic.
  if (isPhone.value) syncMobile(modal, detail, quick, mine)
  else syncDesktop(modal, detail, quick, mine)
}

function syncDesktop(
  modal: ModalRoute | null,
  detail: string | null,
  quick: string | null,
  mine: string | null,
) {
  const readingArticle = isImpactRoute(modal) && detail !== null
  if (readingArticle) {
    const entries = articleEntries(detail)
    // Tapped a mine marker while reading (`?mine=`) → fly to that mine, framed
    // between the article rail and the peek panel; closing returns to the article
    // (§9). The article's own chip (no mine) → reframe the article and highlight
    // the metal's mines alongside it.
    const mineEntries = metalMineEntries(quick, mine)
    if (mineEntries.length) {
      focusEntries(mineEntries, MINE_ZOOM)
    } else {
      fitMarkers(entries, 5)
      setActiveMarkers(quick ? [...entries, ...metalEntries(quick)] : entries)
    }

    return
  }
  // A metal detail — a quick peek (`?metal=`, any route) or a library detail
  // (`/metal/:id`): fly to the tapped mine, or fit all of the metal's mines.
  const detailMetal =
    quick ?? (modal === MODAL_ROUTES.METAL_LIBRARY ? detail : null)
  if (detailMetal) {
    focusMetalDetail(detailMetal, mine)

    return
  }
  // Library list (no selection): clear and stay put — metals span the globe, so
  // fitting them all just flickers.
  if (modal === MODAL_ROUTES.METAL_LIBRARY) {
    setActiveMarkers([])

    return
  }
  if (isImpactRoute(modal)) {
    focusEntries(impactEntries(modal), 5)

    return
  }
  // Everything else — overlays (video / take-action / sources), any future
  // responsive modal, and bare home — drops the highlight and leaves the camera
  // where it is (world recentering is handled on load / intro dismiss / explicit
  // Home). Closing back to the impact route re-highlights it on the next sync.
  setActiveMarkers([])
}

function syncMobile(
  modal: ModalRoute | null,
  detail: string | null,
  quick: string | null,
  mine: string | null,
) {
  // A tapped mine — quick view, library detail, or a peek layered over an article
  // you're reading: fly to that one mine, framed in the strip above the sheet
  // (cameraPadding measures it). First, so it wins even while reading an article —
  // one mine fits the strip where all of the metal's would not, and closing the peek
  // returns to the article.
  const detailMetal =
    quick ?? (modal === MODAL_ROUTES.METAL_LIBRARY ? detail : null)
  const mineEntries = metalMineEntries(detailMetal, mine)
  if (mineEntries.length) {
    focusEntries(mineEntries, MINE_ZOOM)

    return
  }
  // Article detail: highlight it, and reframe only for the marker that opened it
  // (every other entry opens the sheet full, hiding the move), single-region only
  // (a multi-region one won't fit the strip), and never under a chip peek.
  if (isImpactRoute(modal) && detail) {
    const entries = articleEntries(detail)
    setActiveMarkers(entries)
    if (openedFromMarker(detail) && !quick && entries.length === 1)
      fitMarkers(entries, 5)

    return
  }
  // Impact list: highlight that impact's articles (metals mute via CSS) without
  // moving — there's too little room behind the sheet to reframe usefully.
  if (isImpactRoute(modal)) {
    setActiveMarkers(impactEntries(modal))

    return
  }
  // Everything else that covers the map — the metal library (list or a mine-less
  // detail), overlays (video / take-action / sources), any future responsive modal,
  // and bare home — clears the highlight; the camera stays put. (A chip peek over an
  // article is caught by the article branch above, so it keeps that highlight.)
  setActiveMarkers([])
}

watch(
  [
    activeModal,
    activeDetail,
    quickMetal,
    quickMine,
    activeJourney,
    journeyStep,
    isDesktop,
    isPhone,
  ],
  ([modal, detail, quick, mine]) => {
    if (!map) return
    syncMap(modal, detail, quick, mine)
  },
)

// Keep the zoom floor in step if the viewport crosses the phone seam (tablet
// rotate, dev resize) — the map may already be created by the time it flips.
watch(isPhone, (phone) => {
  map?.setMinZoom(minZoomFor(phone))
})

// When intro dismisses, apply the deferred camera sync and handle the bare-home world framing.
watch(showIntro, (now, was) => {
  if (was && !now) {
    syncMap(
      activeModal.value,
      activeDetail.value,
      quickMetal.value,
      quickMine.value,
    )
    if (isBareHome()) flyToWorld()
  }
})

// Handlers, not inline template calls: in the template's type context `map` is
// still at its `null` initializer, so an inline `map?.zoomIn()` can't compile.
const zoomIn = () => map?.zoomIn()
const zoomOut = () => map?.zoomOut()

onUnmounted(() => {
  const metalSnapshot = [...metalMarkers.value]
  const articleSnapshot = [...articleMarkers.value]

  metalMarkers.value = []
  articleMarkers.value = []

  for (const { marker } of metalSnapshot) marker.remove()
  for (const { marker } of articleSnapshot) marker.remove()
  clearJourneyMarkers()

  map?.remove()
})
</script>

<style>
.mapboxgl-ctrl {
  display: none !important;
}

/* Hidden, not muted: ~38 metals' mines at 40% opacity is still a wall of noise. */
[data-journey] .mine-marker:not([data-journey-marker]) {
  display: none;
}

/* The journey's own stay mounted and blank so arriving can animate; `!important`
   because Mapbox writes both properties inline on every render. */
[data-journey] [data-journey-marker] {
  transition: opacity var(--raw-out-quart);
}

[data-journey] [data-journey-marker]:not(.active) {
  opacity: 0 !important;
  pointer-events: none !important;
}

.mine-marker {
  /* Hit Zone */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 1.5rem;
    /*background-color: red;*/
  }

  &.active {
    z-index: 1;
  }

  &:not(.active):hover {
    z-index: 2;
  }
}

.marker-inner {
  transition:
    scale var(--raw-out-quart),
    opacity var(--raw-out-quart);

  .mapboxgl-canvas-container:has(.mine-marker.active)
    .mine-marker:not(.active)
    & {
    scale: 70%;
    opacity: 40%;
  }

  @media (hover: hover) {
    .mapboxgl-canvas-container:has(.mine-marker.active)
      .mine-marker:not(.active):hover
      & {
      scale: 100%;
      opacity: 100%;
    }
  }
}

.marker-bubble {
  background-color: white;
}

/* Bubble + tooltip lift together on hover/active. The transform lives on their
   shared wrapper (the lift), not the bubble, so the sibling tooltip rides along
   too (structure + rationale in createMapMarker). */
.marker-lift {
  transition: transform var(--raw-out-quart);

  .mine-marker.active & {
    transform: translateY(-85%);
  }

  @media (hover: hover) {
    .mine-marker:hover & {
      transform: translateY(-85%);
    }
  }
}

.marker-triangle {
  border-top: 8px solid white;

  position: absolute;
  top: 100%;
  transform: translateY(-85%);
  opacity: 0;

  transition:
    transform var(--raw-out-quart),
    opacity var(--raw-out-quart);

  .mine-marker.active & {
    transform: translateY(0%);
    opacity: 1;
  }

  @media (hover: hover) {
    .mine-marker:hover & {
      transform: translateY(0%);
      opacity: 1;
    }
  }
}

.marker-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: white;

  position: absolute;
  top: 50%;
  transform: translateY(100%);
  opacity: 0;

  transition:
    transform var(--raw-out-quart),
    opacity var(--raw-out-quart);

  .mine-marker.active & {
    transform: translateY(-50%);
    opacity: 1;
  }

  @media (hover: hover) {
    .mine-marker:hover & {
      transform: translateY(-50%);
      opacity: 1;
    }
  }
}

.marker-tooltip {
  color: currentColor;
  text-shadow:
    -1px -1px 0 white,
    1px -1px 0 white,
    -1px 1px 0 white,
    1px 1px 0 white,
    -1px 0 0 white,
    1px 0 0 white,
    0 1px 0 white,
    0 -1px 0 white;

  font-size: 13px;
  line-height: 1.15;
  font-weight: 500;
  white-space: nowrap; /* inherited by both lines, so neither wraps */

  /* Two stacked lines ("{Metal} mine" over the place), centered on the bubble. */
  display: flex;
  flex-direction: column;
  align-items: center;

  position: absolute;
  left: 50%;
  bottom: 100%;
  /* Anchor the box's bottom just above the bubble. A fixed translate(-50%, -50%)
         centred a single line but shifts once the box grows to two, so keep only the
         horizontal centring and hold a constant gap with the margin. */
  transform: translateX(-50%);
  margin-bottom: 6px;
  opacity: 0;

  transition: opacity var(--raw-out-quart);

  .mine-marker.active & {
    opacity: 1;
  }

  @media (hover: hover) {
    .mine-marker:hover & {
      opacity: 1;
    }
  }

  pointer-events: none;
}

.marker-tooltip-primary,
.marker-tooltip-secondary {
  background-color: color-mix(in oklab, white 80%, transparent);
  backdrop-filter: blur(6px);
  box-shadow: 0 0 16px color-mix(in oklab, black 20%, transparent);
  border-radius: 4px;
  padding: 2px 6px;
}

.marker-tooltip-secondary {
  margin-top: 2px;
  color: var(--raw-verydarkgrey);
}
</style>
